/**
 * Hook State — Type definitions and state file helpers
 *
 * State file lives at /tmp/mutagent-hook-<sessionId>.json and tracks:
 *   - traceId / sessionId / startTime
 *   - openSpans: map of spanId → OpenSpan (tool spans currently in-flight)
 *   - parentStack: LIFO stack of open container span IDs
 *     Push on: SessionStart, UserPromptSubmit, SubagentStart, PreCompact
 *     Pop on:  Stop (chain only), SubagentStop, PostCompact, SessionEnd (full cleanup)
 *   - parentStackKinds: parallel array of span kind for each entry in parentStack
 *     Allows Stop/SubagentStop to validate the kind at stack top before popping
 */

import { readFileSync, writeFileSync, renameSync, unlinkSync, existsSync } from 'fs';
import { join } from 'path';
import { tmpdir } from 'os';

// ============================================================================
// Types
// ============================================================================

export interface OpenSpan {
  spanId: string;
  startTime: string;
  toolName: string;
}

export interface HookState {
  traceId: string;
  sessionId: string;
  startTime: string;
  openSpans: Record<string, OpenSpan>;
  /** LIFO stack of open container span IDs. Top = parentStack[parentStack.length - 1]. */
  parentStack: string[];
  /**
   * Parallel array to parentStack — stores the SpanKind for each entry.
   * Always the same length as parentStack. Allows Stop/SubagentStop to
   * validate the kind at the top of the stack before popping.
   */
  parentStackKinds: string[];
}

export interface TracePayload {
  traceId: string;
  sessionId?: string;
  name?: string;
  source: string;
  startTime: string;
  endTime?: string;
  status?: string;
  spans: SpanPayload[];
  metadata?: Record<string, unknown>;
}

export interface SpanPayload {
  spanId: string;
  name: string;
  kind: string;
  startTime: string;
  endTime?: string;
  status?: string;
  input?: string;
  output?: string;
  parentSpanId?: string | null;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// State file helpers
// ============================================================================

export function stateFilePath(sessionId: string): string {
  return join(tmpdir(), `mutagent-hook-${sessionId}.json`);
}

export function readState(sessionId: string): HookState | null {
  const path = stateFilePath(sessionId);
  if (!existsSync(path)) return null;
  try {
    const raw = JSON.parse(readFileSync(path, 'utf-8')) as HookState;
    // Backfill parentStack for states written before v2
    if (!Array.isArray(raw.parentStack)) {
      raw.parentStack = [];
    }
    // Backfill parentStackKinds — keep same length as parentStack
    if (!Array.isArray(raw.parentStackKinds)) {
      raw.parentStackKinds = raw.parentStack.map(() => 'agent');
    }
    return raw;
  } catch {
    return null;
  }
}

/**
 * Atomically write state to disk using a temp file + rename.
 * rename(2) is atomic on POSIX so readers never see a partial write.
 */
export function writeState(sessionId: string, state: HookState): void {
  const path = stateFilePath(sessionId);
  const tmpPath = `${path}.${process.pid.toString()}.tmp`;
  writeFileSync(tmpPath, JSON.stringify(state), 'utf-8');
  renameSync(tmpPath, path);
}

export function deleteState(sessionId: string): void {
  const path = stateFilePath(sessionId);
  if (existsSync(path)) {
    try {
      unlinkSync(path);
    } catch {
      // Ignore cleanup errors
    }
  }
}

// ============================================================================
// Parent stack helpers
// ============================================================================

/**
 * Push a span ID + its kind onto the parent stack.
 * The new span becomes the active parent for subsequent child spans.
 */
export function pushParent(state: HookState, spanId: string, kind: string = 'agent'): HookState {
  state.parentStack.push(spanId);
  state.parentStackKinds.push(kind);
  return state;
}

/**
 * Pop the top span ID from the parent stack, returning [spanId, kind] or null.
 * Mutates state in place. Keeps parentStack and parentStackKinds in sync.
 */
export function popParent(state: HookState): string | null {
  if (state.parentStack.length === 0) return null;
  state.parentStackKinds.pop();
  return state.parentStack.pop() ?? null;
}

/**
 * Peek at the top of the parent stack without removing it.
 * Returns null if the stack is empty.
 */
export function peekParent(state: HookState): string | null {
  if (state.parentStack.length === 0) return null;
  return state.parentStack[state.parentStack.length - 1] ?? null;
}

/**
 * Peek at the kind of the top span on the stack without removing it.
 * Returns null if the stack is empty.
 */
export function peekParentKind(state: HookState): string | null {
  if (state.parentStackKinds.length === 0) return null;
  return state.parentStackKinds[state.parentStackKinds.length - 1] ?? null;
}
